// What the session page (Console D, APS-315) derives from the timeline: each event's links (its
// trace, screen, endpoint, the app start page, its issue), the head's finding ("crashed at 4.31 s,
// 1.37 s after POST /v1/orders returned 500") and the logs' default window.
import type { SessionTimeline } from '../api/types'
import { ATTR } from './conventions'
import type { SessionModel, TimelineEvent } from './session'

export type EventLink =
  | { kind: 'start' }
  | { kind: 'screen'; screen: string }
  | { kind: 'endpoint'; method: string; host: string; template: string }
  | { kind: 'trace'; traceId: string }
  | { kind: 'issue'; id: string }

type SessionIssue = SessionTimeline['issues'][number]

/**
 * The rows that link their trace, as the mock's requests and spans do. An app start or a screen
 * links its own page and a crash its issue, so a trace beside them only widened the column past
 * the table's edge at 1440 (APS-393, Mosaic); the span tree links every trace on its root row.
 */
const TRACED: ReadonlySet<TimelineEvent['signal']> = new Set(['network', 'span'])

/** The issue kinds an event's signal belongs to. */
const ISSUE_KINDS: Partial<Record<TimelineEvent['signal'], string[]>> = {
  crash: ['crash'],
  hang: ['hang', 'anr'],
  error: ['error'],
}

/**
 * The session's one issue for an event, when there is exactly one of its kind: the session's
 * issues carry no event ids, so two crash issues leave a crash without a link (never a guess).
 */
export function issueOf(e: TimelineEvent, issues: SessionIssue[]): SessionIssue | undefined {
  const kinds = ISSUE_KINDS[e.signal]
  if (!kinds) return undefined
  const of = issues.filter((i) => kinds.includes(i.kind))
  return of.length === 1 ? of[0] : undefined
}

/** The links an event's row shows, in the mock's order: app start, screen, trace, endpoint, issue. */
export function eventLinks(e: TimelineEvent, issues: SessionIssue[]): EventLink[] {
  const links: EventLink[] = []
  if (e.signal === 'start') links.push({ kind: 'start' })
  const screen = e.attributes[ATTR.screenName]
  if (e.signal === 'screen' && screen) links.push({ kind: 'screen', screen })
  if (e.traceId && TRACED.has(e.signal)) links.push({ kind: 'trace', traceId: e.traceId })
  const method = e.attributes[ATTR.httpMethod]
  const host = e.attributes[ATTR.serverAddress]
  const template = e.attributes[ATTR.urlTemplate]
  // A failed request links its endpoint, as the mock does; one that worked has its trace.
  if (e.signal === 'network' && e.tone === 'crash' && method && host && template)
    links.push({ kind: 'endpoint', method, host, template })
  const issue = issueOf(e, issues)
  if (issue) links.push({ kind: 'issue', id: issue.id })
  return links
}

/**
 * A log's issue, as the mock links them: a fatal log or one at the crash's moment goes to the
 * session's crash issue, a log during a hang to its hang issue; each only when exactly one issue
 * of that kind is in the session (APS-315).
 */
export function logIssue(
  log: { at: number; severity: string },
  model: SessionModel,
  issues: SessionIssue[],
): SessionIssue | undefined {
  const crash = model.events.find((e) => e.signal === 'crash')
  if (crash && (log.severity === 'fatal' || Math.abs(log.at - crash.at) < 1))
    return issueOf(crash, issues)
  const hang = model.events.find(
    // During it: from its start up to, not at, its end (the log that says it finished).
    (e) => e.signal === 'hang' && log.at >= e.at && log.at < e.at + e.duration,
  )
  return hang ? issueOf(hang, issues) : undefined
}

/** The session's one crash issue, for the head's "Open issue" action. */
export function crashIssue(issues: SessionIssue[]): SessionIssue | undefined {
  const crashes = issues.filter((i) => i.kind === 'crash')
  return crashes.length === 1 ? crashes[0] : undefined
}

/**
 * The issue the head opens as its primary action, as the mock: the session's one crash issue,
 * else its only issue of any kind (an error, a hang); several and no single crash: none (APS-393).
 */
export function headIssue(issues: SessionIssue[]): SessionIssue | undefined {
  return crashIssue(issues) ?? (issues.length === 1 ? issues[0] : undefined)
}

export interface Finding {
  /** When the session ended: the crash, or its last moment. */
  at: number
  /** What came before the end (its likeliest cause), and how long before it. */
  before?: { gap: number; name: string; status: string } | undefined
}

/**
 * The head's finding: when the session ended (the crash for a crashed one) and what came before
 * it, as the mock reads it: "crashed at 4.31 s, 1.37 s after POST /v1/orders returned 500".
 */
export function finding(model: SessionModel): Finding {
  const crash = model.events.find((e) => e.signal === 'crash')
  const at = crash?.at ?? model.length
  const prior = model.events.filter(
    (e) =>
      e !== crash &&
      e.signal !== 'crash' &&
      e.signal !== 'exit' &&
      e.signal !== 'log' &&
      e.at + e.duration <= at,
  )
  // The likeliest cause first, as the mock reads it: the last failed request, then the last
  // error, then whatever came last.
  const last =
    prior.filter((e) => e.signal === 'network' && e.tone === 'crash').at(-1) ??
    prior.filter((e) => e.signal === 'error').at(-1) ??
    prior.at(-1)
  if (!crash || !last) return { at }
  const end = last.at + last.duration
  return {
    at,
    before: {
      gap: Math.max(0, at - end),
      name: last.name,
      status: last.valueKind === 'status' ? last.value : '',
    },
  }
}

/** The logs shown before "All N logs": the last `n` up to the session's end. */
export const LOG_WINDOW = 12

export function logWindow<T>(logs: T[], all: boolean, n = LOG_WINDOW): T[] {
  return all || logs.length <= n ? logs : logs.slice(-n)
}
